/**
 * 今日の過ごし方
 *
 * 睡眠と体調（HRV・Body Battery など）と起床からの時間で、時間帯ごとに頭と体の調子の目安を出し、
 * 頭を使うこと・読書・簡単なこと・運動などのおすすめと、今日やってみる TODO を表示する。
 */

import {
  BookOpen,
  Brain,
  CalendarPlus,
  Check,
  Coffee,
  Dumbbell,
  ListTodo,
  RefreshCw,
  Route,
  Settings,
  Sparkles,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { manualSchedule } from '@/api/services/todo'
import type {
  SleepPlanResponse,
  TodayAdviceResponse,
  TodayBlock,
  TodayCondition,
  TodayEnergy,
  TodaySuggestionKind,
  TodayTodoPick,
} from '@/api/types/sleep'
import { ExerciseAdviceCard } from '@/components/shared/exercise-advice-card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { useSleepStore } from '@/store/useSleepStore'

import { formatJstDate, formatJstTime, formatMinutes, todayJstDate } from '../format'
import { FocusAdviceCard, FocusSlotItem } from './focus-advice'
import { LastNightCard } from './last-night-card'
import { SleepSettingsSheet, TextList, TonightItem, UpcomingNights } from './plan-sections'
import { PrepStepItem } from './prep-step-item'

const KIND_ICON = new Map<TodaySuggestionKind, typeof Brain>([
  ['focus', Brain],
  ['reading', BookOpen],
  ['light', Zap],
  ['workout', Dumbbell],
  ['rest', Coffee],
])

const ENERGY_STYLE = new Map<TodayEnergy, { label: string; className: string; dots: number }>([
  ['high', { label: '集中できる', className: 'text-emerald-600 dark:text-emerald-300', dots: 3 }],
  ['medium', { label: 'ふつう', className: 'text-sky-600 dark:text-sky-300', dots: 2 }],
  ['low', { label: '省エネで', className: 'text-amber-600 dark:text-amber-300', dots: 1 }],
])

const CONDITION_STYLE = new Map<TodayCondition['level'], string>([
  ['good', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  ['normal', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
  ['low', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
])

const timeRange = (start: string, end: string) => `${formatJstTime(start)}〜${formatJstTime(end)}`

const EnergyDots = ({ energy }: { energy: TodayEnergy }) => {
  const style = ENERGY_STYLE.get(energy)
  if (!style) return null
  return (
    <span className={cn('flex items-center gap-1 text-[11px]', style.className)}>
      <span className="flex gap-0.5">
        {[1, 2, 3].map((n) => (
          <span key={n} className={cn('size-1.5 rounded-full', n <= style.dots ? 'bg-current' : 'bg-muted')} />
        ))}
      </span>
      {style.label}
    </span>
  )
}

const BlockItem = ({ block, picks, now }: { block: TodayBlock; picks: TodayTodoPick[]; now: number }) => {
  const past = parseUtcDate(block.end).getTime() < now
  const current = !past && parseUtcDate(block.start).getTime() <= now
  const blockStart = parseUtcDate(block.start).getTime()
  const blockEnd = parseUtcDate(block.end).getTime()
  const scheduledHere = picks.flatMap((p) => {
    if (p.done || !p.scheduled_start || !p.scheduled_end) return []
    const at = parseUtcDate(p.scheduled_start).getTime()
    if (at < blockStart || at >= blockEnd) return []
    return [{ id: p.todo_id, title: p.title, start: p.scheduled_start, end: p.scheduled_end }]
  })
  return (
    <li className={cn('relative space-y-1.5 pb-4 pl-4', past && 'opacity-50')}>
      <span
        className={cn(
          'absolute top-1.5 -left-[5px] size-2.5 rounded-full border-2 border-background',
          current ? 'bg-indigo-500 ring-2 ring-indigo-300' : 'bg-muted-foreground/40',
        )}
      />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="text-sm font-semibold tabular-nums">{timeRange(block.start, block.end)}</span>
        <span className="text-sm">{block.label}</span>
        <EnergyDots energy={block.energy} />
        {current && <span className="text-[11px] text-indigo-600 dark:text-indigo-300">いま</span>}
      </div>
      {block.busy.length > 0 && (
        <p className="text-xs text-muted-foreground">
          予定: {block.busy.join(' / ')}（空き {formatMinutes(block.free_min)}）
        </p>
      )}
      <ul className="space-y-1">
        {scheduledHere.map((p) => (
          <li
            key={`todo-${p.id}`}
            className="flex items-start gap-2 rounded-lg bg-indigo-50 px-2.5 py-1.5 dark:bg-indigo-950/40"
          >
            <ListTodo className="mt-0.5 size-4 shrink-0 text-indigo-600 dark:text-indigo-300" />
            <p className="min-w-0 text-sm">
              <span className="mr-1.5 text-xs tabular-nums">{timeRange(p.start, p.end)}</span>
              {p.title}
            </p>
          </li>
        ))}
        {block.suggestions.map((s) => {
          const Icon = KIND_ICON.get(s.kind) ?? Sparkles
          return (
            <li key={`${s.kind}-${s.title}`} className="flex items-start gap-2 rounded-lg bg-muted/50 px-2.5 py-1.5">
              <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm">
                  {s.title}
                  {s.minutes !== null && (
                    <span className="ml-1 text-xs text-muted-foreground">{formatMinutes(s.minutes)}</span>
                  )}
                </p>
                {s.detail && <p className="text-xs text-muted-foreground">{s.detail}</p>}
              </div>
            </li>
          )
        })}
      </ul>
    </li>
  )
}

const TodoPickItem = ({ pick }: { pick: TodayTodoPick }) => {
  const markPickScheduled = useSleepStore((s) => s.markPickScheduled)
  const [saving, setSaving] = useState(false)
  const { suggested_start: start, suggested_end: end } = pick
  const scheduled = pick.scheduled_start !== null && pick.scheduled_end !== null

  const handleSchedule = async () => {
    if (!start || !end) return
    setSaving(true)
    try {
      await manualSchedule(pick.todo_id, { scheduled_start: start, scheduled_end: end })
      markPickScheduled(pick.todo_id, start, end)
      toast.success(`「${pick.title}」を ${timeRange(start, end)} に入れました`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '予定に入れられませんでした')
    } finally {
      setSaving(false)
    }
  }

  return (
    <li className={cn('flex items-start gap-2 py-2', pick.done && 'opacity-50')}>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className={cn('text-sm font-medium wrap-break-word', pick.done && 'line-through')}>{pick.title}</p>
        <p className="text-xs text-muted-foreground">
          {pick.done ? (
            <span className="mr-1">完了</span>
          ) : (
            pick.scheduled_start &&
            pick.scheduled_end && (
              <span className="mr-1 text-indigo-600 tabular-nums dark:text-indigo-300">
                {timeRange(pick.scheduled_start, pick.scheduled_end)} に入れた
              </span>
            )
          )}
          {!scheduled && !pick.done && start && end && (
            <span className="mr-1 tabular-nums">{timeRange(start, end)}</span>
          )}
          {pick.reason}
        </p>
        {(pick.deadline || pick.project) && (
          <p className="text-[11px] text-muted-foreground">
            {pick.deadline && `締切 ${formatJstDate(pick.deadline)} ${formatJstTime(pick.deadline)}`}
            {pick.deadline && pick.project && '・'}
            {pick.project}
          </p>
        )}
      </div>
      {start && end && !pick.done && (
        <Button
          size="sm"
          variant={scheduled ? 'secondary' : 'outline'}
          disabled={scheduled || saving}
          onClick={() => void handleSchedule()}
          className="shrink-0"
        >
          {scheduled ? <Check /> : <CalendarPlus />}
          {scheduled ? '入れた' : 'この時間に'}
        </Button>
      )}
    </li>
  )
}

const TodayContent = ({
  today,
  plan,
  now,
}: {
  today: TodayAdviceResponse
  plan: SleepPlanResponse | null
  now: number
}) => {
  const visibleBlocks = today.blocks.filter((b) => parseUtcDate(b.end).getTime() >= now)
  const prepItems = plan
    ? plan.prep_steps.flatMap((step) => {
        const at = parseUtcDate(step.at).getTime()
        if (step.key === 'bed') return [{ at, order: 1, node: <TonightItem key="tonight" plan={plan} now={now} /> }]
        return [
          {
            at,
            order: 1,
            node: <PrepStepItem key={step.key} step={step} settings={plan.settings} past={at < now} />,
          },
        ]
      })
    : []
  const pickTitles = new Map(today.todo_picks.map((p) => [p.todo_id, p.title]))
  const focusItems = (today.focus?.slots ?? [])
    .filter((slot) => parseUtcDate(slot.end).getTime() >= now)
    .map((slot) => ({
      at: parseUtcDate(slot.start).getTime(),
      order: 1,
      node: (
        <FocusSlotItem
          key={`focus-${slot.kind}-${slot.start}`}
          slot={slot}
          todoTitles={slot.todo_ids.flatMap((id) => pickTitles.get(id) ?? [])}
        />
      ),
    }))
  const exerciseItems = (today.exercise_slots ?? [])
    .filter((slot) => parseUtcDate(slot.end).getTime() >= now)
    .map((slot) => ({
      at: parseUtcDate(slot.start).getTime(),
      order: 1,
      node: <FocusSlotItem key={`exercise-${slot.kind}-${slot.start}`} slot={slot} todoTitles={[]} />,
    }))
  // 時間帯・寝る準備・集中できる時間・運動の時間を時刻順に並べる。同じ時刻なら時間帯を先にして、他はその中に入って見えるようにする
  const timeline = [
    ...visibleBlocks.map((block) => ({
      at: parseUtcDate(block.start).getTime(),
      order: 0,
      node: <BlockItem key={block.id} block={block} picks={today.todo_picks} now={now} />,
    })),
    ...prepItems,
    ...focusItems,
    ...exerciseItems,
  ].sort((a, b) => a.at - b.at || a.order - b.order)

  return (
    <>
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <span
            className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', CONDITION_STYLE.get(today.condition.level))}
          >
            {today.condition.label}
          </span>
          {today.wake_at && <span className="text-xs text-muted-foreground">起床 {formatJstTime(today.wake_at)}</span>}
        </div>
        <p className="text-sm">{today.summary}</p>
        {today.condition.reasons.length > 0 && (
          <p className="text-xs text-muted-foreground">{today.condition.reasons.join(' / ')}</p>
        )}
      </section>

      {today.focus && <FocusAdviceCard advice={today.focus} now={now} />}

      {today.todo_picks.length > 0 && (
        <section className="space-y-1">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold">
            <ListTodo className="size-4" />
            今日やってみる TODO
          </h3>
          <ul className="divide-y">
            {today.todo_picks.map((pick) => (
              <TodoPickItem key={pick.todo_id} pick={pick} />
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="size-4" />
          時間帯ごとのおすすめ
        </h3>
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">もう寝る準備の時間です</p>
        ) : (
          <ol className="border-l pl-0">{timeline.map((item) => item.node)}</ol>
        )}
      </section>
    </>
  )
}

export const TodayCard = ({ onOpenRecord }: { onOpenRecord: (date: string) => void }) => {
  const { today, isTodayLoading, isTodayGenerating, fetchToday, generateToday, plan, lastNight } = useSleepStore()
  const [now, setNow] = useState(() => Date.now())
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    void fetchToday()
  }, [fetchToday])

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  return (
    <div className="space-y-4">
      {lastNight?.date === todayJstDate() && (
        <LastNightCard day={lastNight} onOpenRecord={() => onOpenRecord(lastNight.date)} />
      )}
      {today ? (
        <section className={cn('space-y-4 rounded-xl border bg-card p-4 shadow-sm', isTodayGenerating && 'opacity-60')}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold">今日の過ごし方</h2>
              <p className="text-xs text-muted-foreground">
                {isTodayGenerating
                  ? '今の体調と予定で作り直しています…'
                  : `${formatJstTime(today.generated_at)} に作成`}
                {today.model ? '' : '（AI なし）'}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={isTodayGenerating} onClick={() => void generateToday()}>
                <RefreshCw className={cn(isTodayGenerating && 'animate-spin')} />
                作り直す
              </Button>
              <Button
                size="icon"
                variant="ghost"
                aria-label="睡眠の計画の設定"
                disabled={!plan}
                onClick={() => setSettingsOpen(true)}
              >
                <Settings />
              </Button>
            </div>
          </div>
          <TodayContent today={today} plan={plan} now={now} />
        </section>
      ) : (
        (isTodayLoading || isTodayGenerating) && (
          <div className="space-y-2">
            <p className="text-center text-sm text-muted-foreground">今日の体調と予定から作っています…</p>
            <Skeleton className="h-96" />
          </div>
        )
      )}
      {plan && (
        <section className="space-y-4 rounded-xl border bg-card p-4 shadow-sm">
          <UpcomingNights nights={plan.upcoming} />
          <TextList title="予定に合わせるコツ" icon={Route} items={plan.schedule_tips} />
          <TextList title="寝る前に避けたいこと" icon={X} items={plan.avoid} />
        </section>
      )}
      {plan?.exercise && <ExerciseAdviceCard advice={plan.exercise} />}
      {plan && <SleepSettingsSheet settings={plan.settings} open={settingsOpen} onOpenChange={setSettingsOpen} />}
    </div>
  )
}
